import RatingStars from '@components/partials/site/rating/ratingStars';
import styles from '@styles/scss/category/categoryTopSites.module.scss';
import { TopSite } from 'lib/types/item/topSiteType';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { parseCookies } from 'nookies';
import React from 'react';
import { useEffect, useState } from 'react';

export default function CategoryTopSiteCard({
    site,
    languageFile,
    index,
    categoryName,
    activeSites,
    currentLang
}: {
    site: TopSite;
    languageFile: any;
    index: number;
    categoryName: string;
    activeSites: any;
    currentLang: string;
}): JSX.Element {
    const cookies = parseCookies();
    const [updatedTourlink, setUpdatedTourlink] = useState(site.tourlink);
    const [isExpanded, setIsExpanded] = useState(false);
    const router = useRouter();

    useEffect(() => {
        if (cookies.u) {
            setUpdatedTourlink(site.tourlink.replace('{exid}', cookies.u));
        }
    }, [cookies.u, site.tourlink]);

    useEffect(() => {
      setIsExpanded(false);
  }, [router.locale]);

    const externalLink = site.useOpensite ? '/out/' + site.siteSlug : updatedTourlink;
    const internalLink = `/${site.categorySlug}/${site.siteSlug}`;
    const [showPros, setShowPros] = useState(true);
    const [showCons, setShowCons] = useState(false);

    const showProsSection = () => {
        setShowPros(true);
        setShowCons(false);
    };

    const showConsSection = () => {
        setShowPros(false);
        setShowCons(true);
    };

    const toggleReadMore = () => {
        setIsExpanded(!isExpanded);
    };

    const maxLength = currentLang === 'ja' ? 75 : 150; 
    const isLongDescription = site.minidescription.length > maxLength;
    const displayedDescription = isExpanded 
    ? site.minidescription 
    : site.minidescription.substring(0, maxLength) + (isLongDescription ? '...' : '');
    return (
        <div className={styles.topSite} key={site.siteSlug}>
            <div className={styles.topSiteTitle}>
                <span>#{index + 1} </span> <h2>{site.siteName}</h2>
            </div>
            <div className={styles.topSiteCard}>
                <div className={styles.image}>
                    <Link prefetch={false} href={externalLink}>
                        <a
                            data-site-name={site.siteName}
                            data-site-category={categoryName}
                            target="_blank"
                            rel={
                                site.useOpensite
                                    ? 'nofollow noopener noreferrer'
                                    : 'noopener noreferrer'
                            }
                            data-event-category="User Action"
                            data-event-action="outbound_click"
                            data-event-label={site.siteName}>
                            <Image
                                className={styles.siteImg}
                                src={site.image}
                                alt={site.siteName}
                                width={268}
                                height={105}
                                unoptimized={true}
                                objectFit="cover"
                            />
                        </a>
                    </Link>
                </div>
                <div className={styles.cardContent}>
                <div className={styles.descriptionContainer}>
                  <div className={isExpanded ? styles.expandedDescription : ''}>
                      <div dangerouslySetInnerHTML={{ __html: displayedDescription }} />
                          <button className={styles.readMore} onClick={toggleReadMore}>
                              {isExpanded ? (
                                  <>
                                    {languageFile.read_less} 
                                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12.6667 9.5L7.66675 4.5L2.66675 9.5" stroke="#090909" stroke-miterlimit="10" stroke-linecap="square" stroke-linejoin="round"/>
                                    </svg>
                              </>
                              ) : (
                                  <>
                                      {languageFile.read_more}
                                      <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M12.6666 5.5L7.66663 10.5L2.66663 5.5" stroke="#090909" stroke-miterlimit="10" stroke-linecap="square" stroke-linejoin="round"/>
                                      </svg>
                                  </>
                              )}
                          </button>
                    </div>
                  </div>
                  <div className={isExpanded ? styles.ratingExpanded : styles.rating}>
                      <RatingStars siteReviewData={site} />
                  </div>
                    <div className={styles.prosCons}>
                        <div className={styles.prosConsTitle}>
                            <button
                                className={showPros ? styles.activeGreen : ''}
                                onClick={showProsSection}>
                                <svg
                                    width="31"
                                    height="30"
                                    viewBox="0 0 31 30"
                                    fill="none"
                                    xmlns="https://www.w3.org/2000/svg">
                                    <path
                                        d="M29.2187 16.9939C29.9061 16.2065 30.25 15.2753 30.25 14.2003C30.25 13.2253 29.8933 12.3817 29.1822 11.6688C28.4698 10.956 27.6261 10.5998 26.6512 10.5998H21.4577C21.5076 10.4248 21.5576 10.2748 21.6075 10.1499C21.657 10.0249 21.7264 9.88723 21.8136 9.73723C21.9008 9.58717 21.9635 9.47454 22.0009 9.39968C22.2258 8.97497 22.398 8.62474 22.5169 8.34997C22.6356 8.07467 22.7538 7.69982 22.8728 7.22481C22.9918 6.75013 23.0509 6.27493 23.0509 5.79992C23.0509 5.50006 23.0473 5.25615 23.0414 5.06872C23.0355 4.88116 23.0037 4.59982 22.948 4.22484C22.8914 3.84985 22.8162 3.53738 22.7227 3.2875C22.6288 3.03755 22.4787 2.75608 22.2729 2.4438C22.0665 2.13094 21.8166 1.87817 21.5226 1.6843C21.2285 1.49057 20.8537 1.3281 20.3975 1.19702C19.9408 1.06567 19.4255 1 18.8501 1C18.5251 1 18.2441 1.11887 18.0064 1.3564C17.7564 1.60635 17.5435 1.91882 17.3687 2.29368C17.1936 2.66867 17.0717 2.99374 17.0029 3.26891C16.9341 3.54381 16.8558 3.92517 16.7687 4.41253C16.6558 4.93777 16.5718 5.31565 16.5152 5.54688C16.4591 5.77818 16.3494 6.08126 16.1871 6.45618C16.0244 6.83137 15.8309 7.13116 15.6059 7.35628C15.1935 7.76871 14.5622 8.51868 13.712 9.60622C13.0993 10.4061 12.4682 11.1623 11.8182 11.8748C11.168 12.5875 10.6932 12.9562 10.3932 12.9812C10.0809 13.0061 9.81214 13.1342 9.58708 13.3656C9.36196 13.5968 9.24953 13.8688 9.24953 14.1813V26.1999C9.24953 26.5249 9.36813 26.8028 9.60567 27.0341C9.84307 27.2655 10.1243 27.3874 10.4494 27.3999C10.8867 27.4124 11.8743 27.6872 13.4118 28.2252C14.3743 28.5496 15.1272 28.7969 15.671 28.9654C16.2147 29.1339 16.9746 29.3153 17.9488 29.5093C18.924 29.703 19.8237 29.8001 20.6486 29.8001H23.0674C24.7302 29.7751 25.9609 29.2875 26.7612 28.3377C27.486 27.4751 27.7921 26.3439 27.68 24.9438C28.1674 24.4814 28.5052 23.8941 28.6924 23.1815C28.9045 22.4194 28.9045 21.6881 28.6924 20.9882C29.2672 20.2257 29.536 19.3694 29.4982 18.4194C29.4998 18.019 29.4062 17.5442 29.2184 16.9939H29.2187ZM6.85006 13.0002H1.45023C1.12502 13.0002 0.843743 13.1191 0.606338 13.3566C0.368735 13.594 0.25 13.8752 0.25 14.2003V26.2007C0.25 26.5252 0.368867 26.8066 0.606338 27.0444C0.844071 27.2814 1.12528 27.4003 1.45023 27.4003H6.85006C7.17501 27.4003 7.45621 27.2814 7.69375 27.0444C7.93122 26.8066 8.04996 26.5253 8.04996 26.2007V14.2002C8.04996 13.8751 7.93122 13.594 7.69382 13.3565C7.45628 13.1189 7.17514 13.0002 6.85013 13.0002L6.85006 13.0002ZM4.69371 24.6534C4.45624 24.8845 4.17503 25.0001 3.85002 25.0001C3.51247 25.0001 3.2281 24.8845 2.99694 24.6534C2.76577 24.4222 2.65019 24.1378 2.65019 23.8003C2.65019 23.4753 2.76564 23.194 2.99694 22.9566C3.2281 22.7191 3.51247 22.6002 3.85002 22.6002C4.1751 22.6002 4.45618 22.7191 4.69371 22.9567C4.93125 23.1941 5.05005 23.4755 5.05005 23.8004C5.05005 24.138 4.93158 24.4222 4.69378 24.6535L4.69371 24.6534Z"
                                        fill="#C6C6C6"
                                    />
                                </svg>
                                <span>Pros</span>
                            </button>
                            <button
                                className={showCons ? styles.activeRed : ''}
                                onClick={showConsSection}>
                                <svg
                                    width="31"
                                    height="30"
                                    viewBox="0 0 31 30"
                                    fill="none"
                                    xmlns="https://www.w3.org/2000/svg">
                                    <path
                                        d="M6.68292 3.00013H1.28321C0.958137 3.00013 0.677066 3.119 0.439338 3.35646C0.201741 3.5938 0.0830078 3.87506 0.0830078 4.19994V16.2C0.0830078 16.5248 0.201872 16.8062 0.439338 17.0436C0.676803 17.2809 0.958005 17.3998 1.28321 17.3998H6.68292C7.00786 17.3998 7.28906 17.281 7.52659 17.0436C7.76406 16.8063 7.88279 16.5249 7.88279 16.1999V4.19994C7.88279 3.87513 7.76406 3.59393 7.52666 3.35646C7.28913 3.11873 7.00799 3.00007 6.68299 3.00007L6.68292 3.00013ZM4.52662 7.44388C4.28916 7.68148 4.00795 7.80014 3.68295 7.80014C3.3454 7.80014 3.06105 7.68148 2.82989 7.44395C2.59872 7.20648 2.48314 6.92534 2.48314 6.60008C2.48314 6.26279 2.59859 5.9783 2.82989 5.74701C3.06105 5.51585 3.3454 5.40027 3.68295 5.40027C4.00802 5.40027 4.28909 5.51585 4.52662 5.74701C4.76435 5.97817 4.88295 6.26279 4.88295 6.60008C4.88295 6.92515 4.76422 7.20622 4.52669 7.44395L4.52662 7.44388ZM29.0508 13.4061C29.2386 12.8561 29.3319 12.3812 29.3305 11.9816C29.3684 11.0316 29.0995 10.1754 28.5242 9.41295C28.7368 8.7129 28.7368 7.98153 28.5242 7.21909C28.3373 6.50656 27.9993 5.91933 27.5121 5.45681C27.6244 4.05671 27.3183 2.92566 26.5933 2.06308C25.7933 1.11315 24.5621 0.62541 22.8996 0.600586H20.4809C19.6561 0.600586 18.7558 0.69745 17.781 0.891179C16.8065 1.08491 16.0469 1.26629 15.5035 1.43493C14.9597 1.60371 14.2065 1.85063 13.2442 2.1755C11.7067 2.71289 10.7192 2.98785 10.2818 3.00033C9.95674 3.013 9.67554 3.13469 9.43807 3.36612C9.20054 3.59708 9.08174 3.87533 9.08174 4.20027V16.2187C9.08174 16.5311 9.1945 16.803 9.41929 17.0341C9.64421 17.2655 9.91301 17.3937 10.2255 17.4188C10.5253 17.444 11.0004 17.8125 11.6504 18.5249C12.3005 19.2374 12.9316 19.9938 13.5441 20.7938C14.3941 21.881 15.0255 22.6309 15.4378 23.0435C15.6627 23.2682 15.8561 23.5682 16.0189 23.9433C16.1812 24.3182 16.2909 24.6213 16.3475 24.8526C16.4036 25.0838 16.4876 25.4617 16.6007 25.9871C16.6879 26.4744 16.7661 26.8557 16.8351 27.1305C16.9038 27.4056 17.0253 27.7306 17.2004 28.1054C17.3758 28.4808 17.5881 28.793 17.8382 29.0428C18.0756 29.2805 18.3569 29.3994 18.6819 29.3994C19.2572 29.3994 19.7723 29.3337 20.2291 29.2025C20.6852 29.0713 21.0601 28.909 21.354 28.7153C21.648 28.5211 21.8979 28.2686 22.1043 27.9558C22.3107 27.6434 22.4605 27.362 22.5542 27.112C22.6481 26.8616 22.7233 26.5497 22.7792 26.1749C22.8354 25.7995 22.8671 25.5182 22.8731 25.3307C22.8793 25.1433 22.8827 24.8997 22.8827 24.5994C22.8827 24.1246 22.8204 23.6465 22.6955 23.1651C22.5701 22.6842 22.4512 22.3183 22.3389 22.0679C22.2266 21.818 22.0578 21.462 21.8329 20.9995C21.7952 20.9244 21.7329 20.8116 21.6455 20.6617C21.5583 20.5117 21.4891 20.3739 21.4391 20.249C21.389 20.1241 21.3392 19.9741 21.2894 19.7991H26.4831C27.4578 19.7991 28.2985 19.4426 29.0046 18.7299C29.711 18.0176 30.0703 17.174 30.0829 16.199C30.0819 15.0995 29.7382 14.1682 29.0506 13.4058L29.0508 13.4061Z"
                                        fill="#C6C6C6"
                                    />
                                </svg>
                                <span>Cons</span>
                            </button>
                        </div>
                        <div className={styles.prosConsText}>
                            {showPros && (
                                <ul
                                    className={styles.text}
                                    dangerouslySetInnerHTML={{ __html: site.pros }}></ul>
                            )}
                            {showCons && (
                                <ul
                                    className={styles.text}
                                    dangerouslySetInnerHTML={{ __html: site.cons }}></ul>
                            )}
                        </div>
                    </div>

                    <Link prefetch={false} href={externalLink}>
                        <a
                            className={styles.visitButton}
                            data-site-name={site.siteName}
                            data-site-category={categoryName}
                            target="_blank"
                            rel={
                                site.useOpensite
                                    ? 'nofollow noopener noreferrer'
                                    : 'noopener noreferrer'
                            }
                            data-event-category="User Action"
                            data-event-action="outbound_click"
                            data-event-label={site.siteName}>
                            {languageFile.visit_website}
                        </a>
                    </Link>
                    {activeSites.map((activeSite: any) => {
                      if (activeSite.siteName === site.siteName) {
                          return activeSite.siteReviewActive ? (
                              <Link prefetch={false} href={internalLink} key={activeSite.siteName}>
                                  <a
                                      className={styles.readButton}
                                      data-site-name={site.siteName}
                                      data-site-category={categoryName}
                                      target="_blank"
                                      rel={
                                          site.useOpensite
                                              ? 'nofollow noopener noreferrer'
                                              : 'noopener noreferrer'
                                      }>
                                      {languageFile.read_review}
                                  </a>
                              </Link>
                          ) : 
                              <span
                              className={`${styles.readButton} ${styles.greyedOutReadButton}`}>
                                  {languageFile.review_soon}
                              </span>
                      }
                      return null;
                  })}
                </div>
            </div>
        </div>
    );
}
